import { useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useNow } from "@/shared/hooks/useNow";
import useTranslations from "@/shared/lang/useTranslations";
import { readHumanErrorMessage } from "@/shared/utils/apiError";
import { canUseEmailBackup, STEP_UP_EMAIL_REQUEST, stepUpEmailCode } from "@/shared/utils/stepUp";
import {
  STEP_UP_EMAIL_CODE_INVALID_CODE,
  STEP_UP_EMAIL_COOLDOWN_CODE,
  STEP_UP_EMAIL_NOT_ENABLED_CODE,
  STEP_UP_EMAIL_SENT_CODE,
  stepUpEmailCooldownSeconds,
  stepUpRefusalCode,
} from "@/shared/utils/twoFactorErrors";

/** The backend lets another code out a minute after the last one (`STEP_UP_EMAIL_COOLDOWN_SECONDS`). */
const EMAIL_RESEND_SECONDS = 60;

interface ResendWindow {
  /** When another code may be asked for. */
  at: number;
  /** How long the wait was when it started, so a stale clock never shows more than that. */
  total: number;
}

export interface EmailCodeChallenge {
  /** The backup is on for this user: the method faces offer it. */
  offered: boolean;
  /** The challenge shows the "check your email" face. */
  active: boolean;
  /** The sentence the code went out with ("we sent a 6-digit code to j***@acme.io…"). */
  message: string;
  code: string;
  setCode: (code: string) => void;
  /** Why the last code was refused, or why no code could be sent from this face. */
  error: string | null;
  /** A quiet confirmation, e.g. that a new code is on its way. */
  notice: string | null;
  /** Bumps on every refusal so the field shakes once more. */
  refusals: number;
  /** The code is dead (expired, used, out of attempts): asking for another one is the way on. */
  needsNewCode: boolean;
  /** Why the method face could not get a code (cooldown from another action, the backup turned off…). */
  requestError: string | null;
  verifying: boolean;
  requesting: boolean;
  /** A request is out: nothing may close the challenge or send another. */
  busy: boolean;
  /** Milliseconds until another code may be asked for; 0 when it may be asked for now. */
  resendIn: number;
  /** Milliseconds the method face waits before it can offer a code (no code sent for this action yet). */
  offerWaitIn: number;
  /** From a method face: back to the code already sent, or ask for one. */
  offer: () => void;
  resend: () => void;
  verify: (value: string) => void;
  /** Back to the PIN or the authenticator; the code sent stays valid and the offer returns to it. */
  back: () => void;
}

/**
 * The email backup inside a step-up challenge. It runs the same pending action the challenge guards, with
 * `x-two-factor-code: email` to have a code sent for it (the server answers 401 `STEP_UP_EMAIL_SENT`, which
 * here is the success that turns the challenge into "check your email"), then with `email:NNNNNN` to approve
 * it. Every other answer keeps the challenge open with what to do next.
 *
 * The state lives with the challenge, not with a face: a caller that closes the challenge while the request
 * runs (`closeOnSend`) and reopens it on a wrong code finds the email face where it was. Only a fresh opening
 * starts from the method again.
 */
export function useEmailCodeChallenge(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  onSubmit: (code: string) => Promise<void>,
): EmailCodeChallenge {
  const { t } = useTranslations("global");
  const { user } = useAuth();
  const offered = canUseEmailBackup(user);

  const [showing, setShowing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const [needsNewCode, setNeedsNewCode] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [resendWindow, setResendWindow] = useState<ResendWindow | null>(null);
  const busy = verifying || requesting;

  // A new opening is a new action, and a code is bound to the action it was asked for. Not when the caller
  // reopens the challenge to show a refused code (`closeOnSend`): that request is still being answered.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open && !busy) {
      setShowing(false);
      setMessage(null);
      setCode("");
      setError(null);
      setNotice(null);
      setNeedsNewCode(false);
      setRequestError(null);
      setResendWindow(null);
    }
  }

  const now = useNow(open && resendWindow !== null);
  const resendIn = resendWindow ? Math.min(Math.max(0, resendWindow.at - now), resendWindow.total) : 0;

  const startResendWindow = (seconds: number) => {
    setResendWindow({ at: Date.now() + seconds * 1000, total: seconds * 1000 });
  };

  const request = async (fromFace: boolean) => {
    if (busy) return;
    setRequesting(true);
    setRequestError(null);
    setError(null);
    setNotice(null);
    try {
      await onSubmit(STEP_UP_EMAIL_REQUEST);
      // Resolved without a code: the caller closed the challenge and handles the outcome where the action lives.
      onOpenChange(false);
    } catch (err: unknown) {
      const refusal = stepUpRefusalCode(err);
      const say = (text: string) => (fromFace ? setError(text) : setRequestError(text));
      if (refusal === STEP_UP_EMAIL_SENT_CODE) {
        setMessage(readHumanErrorMessage(err, t("step_up_email_description_fallback")));
        setShowing(true);
        setCode("");
        setNeedsNewCode(false);
        setNotice(fromFace ? t("step_up_email_resent") : null);
        startResendWindow(EMAIL_RESEND_SECONDS);
      } else if (refusal === STEP_UP_EMAIL_COOLDOWN_CODE) {
        // Too soon after the last code, or six this hour. That code may belong to another action, so the
        // method face says how long to wait rather than send the person to an inbox with nothing for this one.
        startResendWindow(stepUpEmailCooldownSeconds(err) ?? EMAIL_RESEND_SECONDS);
        say(readHumanErrorMessage(err, t("step_up_email_error_generic")));
      } else if (refusal === STEP_UP_EMAIL_NOT_ENABLED_CODE) {
        say(readHumanErrorMessage(err, t("step_up_email_not_enabled")));
      } else {
        say(readHumanErrorMessage(err, t("step_up_email_error_generic")));
      }
    } finally {
      setRequesting(false);
    }
  };

  const verify = async (value: string) => {
    if (value.length !== 6 || busy) return;
    setVerifying(true);
    setError(null);
    setNotice(null);
    try {
      await onSubmit(stepUpEmailCode(value));
      onOpenChange(false);
    } catch (err: unknown) {
      const refusal = stepUpRefusalCode(err);
      if (refusal === STEP_UP_EMAIL_CODE_INVALID_CODE) {
        setNeedsNewCode(true);
        setError(readHumanErrorMessage(err, t("step_up_email_code_invalid")));
      } else if (refusal === STEP_UP_EMAIL_NOT_ENABLED_CODE) {
        setError(readHumanErrorMessage(err, t("step_up_email_not_enabled")));
      } else {
        // A wrong code: the backend's sentence says how many attempts this code has left.
        setError(readHumanErrorMessage(err, t("two_factor_challenge_error_invalid_code")));
      }
      setRefusals((n) => n + 1);
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return {
    offered,
    active: showing && message !== null,
    message: message ?? t("step_up_email_description_fallback"),
    code,
    setCode,
    error,
    notice,
    refusals,
    needsNewCode,
    requestError,
    verifying,
    requesting,
    busy,
    resendIn,
    offerWaitIn: message === null ? resendIn : 0,
    offer: () => {
      if (busy) return;
      // The code sent moments ago is still good for this action: go back to it instead of asking again.
      if (message !== null) {
        setRequestError(null);
        setShowing(true);
        return;
      }
      if (resendIn > 0) return;
      void request(false);
    },
    resend: () => void request(true),
    verify: (value) => void verify(value),
    back: () => {
      if (busy) return;
      setShowing(false);
      setError(null);
      setNotice(null);
      setCode("");
    },
  };
}
